import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { CreditCard, Download, ChevronsUpDown, Building, Banknote, Receipt, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/hooks/use-toast";
import { formatDate, formatUnixTimestamp, formatPrice } from "@/utils/dateFormatter";

import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import SetupForm from './SetupForm';
import {PUBLISHABLE_KEY} from "@/integrations/stripe/key";
import PaymentStatus from "./PaymentStatus";
import { PlanSelection } from "./PlanSelection";
import { CurrentSubscription } from "./CurrentSubscription";

const stripePromise = loadStripe(PUBLISHABLE_KEY);

// Organization data schema
const organizationSchema = z.object({
    name: z.string().min(1, "Organization name is required"),
    invoice_email: z.string().email("Valid email is required").min(1, "Invoice email is required"),
    vat_number: z.string().min(1, "VAT number is required"),
    attention: z.string().optional(),
    street: z.string().optional(),
    city: z.string().optional(),
    zip: z.string().optional(),
    country: z.string().min(1, "Country is required"),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

// Address interface for JSONB structure
interface Address {
    attention?: string;
    street?: string;
    city?: string;
    zip?: string;
    country: string;
}

// Types for Stripe API responses
interface StripePlan {
    product_id: string;
    product_name: string;
    description: string | null;
    price_id: string;
    currency: string;
    unit_amount: number;
    product_attrs: {
        id: string;
        name: string;
        type: string;
        active: boolean;
        description: string | null;
        default_price: string;
    };
    price_attrs: {
        id: string;
        type: string;
        active: boolean;
        currency: string;
        recurring: {
            interval: string;
            interval_count: number;
            usage_type: string;
        };
        lookup_key: string;
        unit_amount: number;
        unit_amount_decimal: string;
    };
}

interface StripeSubscription {
    subscription: {
        id: string;
        status: string;
        currency: string;
        quantity: number;
        current_period_start: number;
        current_period_end: number;
        cancel_at_period_end: boolean;
        billing_cycle_anchor: number;
        discounts: string[];
        items: {
            data: Array<{
                current_period_start: number;
                current_period_end: number;
                id: string;
                quantity: number;
                price: {
                    id: string;
                    unit_amount: number;
                    currency: string;
                    recurring: {
                        interval: string;
                        interval_count: number;
                    };
                    lookup_key: string;
                    product: string;
                };
                plan: {
                    id: string;
                    amount: number;
                    currency: string;
                    interval: string;
                    interval_count: number;
                    product: string;
                };
            }>;
        };
        plan: {
            id: string;
            amount: number;
            currency: string;
            interval: string;
            interval_count: number;
            product: string;
        };
    };
}

// Types for Stripe invoice responses
interface StripeInvoice {
    id: string;
    object: string;
    amount_due: number;
    amount_paid: number;
    amount_remaining: number;
    created: number;
    currency: string;
    customer_name: string;
    customer_email: string;
    hosted_invoice_url: string;
    invoice_pdf: string;
    number: string;
    status: string;
    total: number;
    subtotal: number;
    billing_reason: string;
    period_start: number;
    period_end: number;
    lines: {
        data: Array<{
            description: string;
            amount: number;
            quantity: number;
        }>;
    };
}

export const Billing = () => {
    const { t } = useTranslation();
    const { user, currentOrganization, refreshUserData } = useAuth();
    const [selectedPlan, setSelectedPlan] = useState("");
    const [selectedPriceId, setSelectedPriceId] = useState("");
    const [discountCode, setDiscountCode] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [expiryDate, setExpiryDate] = useState("04/26");
    const [organizationData, setOrganizationData] = useState<any>(null);
    const [isLoadingOrg, setIsLoadingOrg] = useState(true);
    const [isSubmittingOrg, setIsSubmittingOrg] = useState(false);
    const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<string[]>([]);
    const [expandedAccordionItems, setExpandedAccordionItems] = useState<string[]>([]);
    const [isAStripeCustomer, setIsAStripeCustomer] = useState(false);
    const [hasAPaymentMethod, setHasAPaymentMethod] = useState(false);
    const [hasAStripeSubscription, setHasAStripeSubscription] = useState(false);
    const [isSetupOfPaymentMethod, setIsSetupOfPaymentMethod] = useState(false);
    const [paymentIntentData, setPaymentIntentData] = useState<{
        client_secret: string;
        customer_session_client_secret: string;
    } | null>(null);
    const [isLoadingPaymentIntent, setIsLoadingPaymentIntent] = useState(false);
      // New state for plan management
    const [availablePlans, setAvailablePlans] = useState<StripePlan[]>([]);
    const [currentSubscription, setCurrentSubscription] = useState<StripeSubscription | null>(null);
    const [activeLicenses, setActiveLicenses] = useState<number>(0);
    const [isLoadingPlans, setIsLoadingPlans] = useState(false);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
    const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
    const [showUpdatePlan, setShowUpdatePlan] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
      // State for invoices
    const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [isExportingInvoices, setIsExportingInvoices] = useState(false);

    // Form for organization data
    const form = useForm<OrganizationFormData>({
        resolver: zodResolver(organizationSchema),
        defaultValues: {
            name: "",
            invoice_email: "",
            vat_number: "",
            attention: "",
            street: "",
            city: "",
            zip: "",
            country: "Belgium",
        },
    });    // Fetch organization data and subscription info
    useEffect(() => {
        if (currentOrganization?.id) {
            fetchOrganizationData();
            fetchSubscriptionData();
            fetchAvailablePlans();
            fetchActiveLicenses();
            isPaymentMethodSetup();
        }
    }, [currentOrganization]);

    // Fetch data when subscription status changes
    useEffect(() => {
        if (currentOrganization?.id && hasAStripeSubscription) {
            fetchCurrentSubscription();
            fetchInvoices();
        }
    }, [currentOrganization, hasAStripeSubscription]);

    const fetchOrganizationData = async () => {
        if (!currentOrganization?.id) return;

        setIsLoadingOrg(true);
        try {
            const { data, error } = await supabase
                .from('organizations')
                .select('*')
                .eq('id', currentOrganization.id)
                .single();

            if (error) {
                console.error("Error fetching organization:", error);
                throw error;
            } if (data) {
                setOrganizationData(data);
                const address: Address = (data.address as Address) || {
                    attention: "",
                    street: "",
                    city: "",
                    zip: "",
                    country: "Belgium"
                };

                // Update form with organization data
                form.reset({
                    name: data.name || "",
                    invoice_email: data.invoice_email || "",
                    vat_number: data.vat_number || "",
                    attention: address.attention || "",
                    street: address.street || "",
                    city: address.city || "",
                    zip: address.zip || "",
                    country: address.country || "Belgium",
                });
            }
        } catch (error: any) {
            console.error("Error fetching organization data:", error);
            toast({
                title: "Error",
                description: "Failed to load organization data",
                variant: "destructive",
            });
        } finally {
            setIsLoadingOrg(false);
        }
    };

    const onSubmitOrganization = async (data: OrganizationFormData) => {
        if (!currentOrganization?.id) {
            toast({
                title: "Error",
                description: "No organization selected",
                variant: "destructive",
            });
            return;
        }

        setIsSubmittingOrg(true);

        try {
            const address: Address = {
                attention: data.attention,
                street: data.street,
                city: data.city,
                zip: data.zip,
                country: data.country,
            };

            const { error } = await supabase
                .from('organizations')
                .update({
                    name: data.name,
                    invoice_email: data.invoice_email,
                    vat_number: data.vat_number,
                    address: address,
                    updated_at: new Date().toISOString(),
                })
                .eq('id', currentOrganization.id);

            if (error) {
                console.error("Error updating organization:", error);
                throw error;
            }

            // invoke function stripe_customer, with the organization id as post parameter
            let { data: stripeCustomerData, error: stripeCustomerError } = await supabase.functions.invoke('stripe-customer', {
                method: 'POST',
                body: JSON.stringify({ organization: currentOrganization.id }),
            });

            if (stripeCustomerError) {
                console.error("Error creating Stripe customer:", stripeCustomerError);
                throw stripeCustomerError;
            }

            window.location.href = window.location.href;

            toast({
                title: "Success",
                description: "Organization data updated successfully",
            });

            // Refresh the data
            await fetchOrganizationData();
        } catch (error: any) {
            console.error("Error updating organization:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to update organization data",
                variant: "destructive",
            });
        } finally {
            setIsSubmittingOrg(false);
        }
    };    
    
    
    const isPaymentMethodSetup = () => {

        const sucessPaymentSetup = new URLSearchParams(window.location.search).get(
            'payment_intent_setup_succeeded'
        );

        if (sucessPaymentSetup === "true") {
            setIsSetupOfPaymentMethod(true);
            setPaymentIntentData(null); // Clear payment intent data to return to normal state
            toast({
                title: "Success",
                description: "Payment method setup completed successfully.",
            });
            // Refresh subscription data to get the new payment method
            fetchSubscriptionData();
        } else {
            setIsSetupOfPaymentMethod(false);
        }
    };

    // Fetch subscription data to determine allowed payment methods
    const fetchSubscriptionData = async () => {

        if (!currentOrganization?.id) return;

        try {
            const { data, error } = await supabase
                .from('organization_subscription')
                .select('allowed_payment_methods, stripe_customer_id, stripe_subscription_id, stripe_payment_method_id')
                .eq('id', currentOrganization.id)
                .single();

            if (error) {
                console.error("Error fetching subscription data:", error);
                // If no subscription data found, default to all payment methods
                const defaultMethods = ["credit_card"];
                setAllowedPaymentMethods(defaultMethods);
                setExpandedAccordionItems(defaultMethods);
                return;
            }
            if (data) {

                // Use allowed payment methods from database, or default to all if null
                const paymentMethods = data.allowed_payment_methods || ["credit_card"];
                setAllowedPaymentMethods(paymentMethods);
                setExpandedAccordionItems(paymentMethods); // Auto-expand allowed methods
                setIsAStripeCustomer(data.stripe_customer_id ? true : false);
                setHasAStripeSubscription(data.stripe_subscription_id ? true : false);
                setHasAPaymentMethod(data.stripe_payment_method_id ? true : false);

                if (data.stripe_payment_method_id) {
                    // Fetch payment method details
                    await getPaymentMethodData(data.stripe_payment_method_id);
                }
            }

        } catch (error) {
            console.error("Failed to fetch subscription data:", error);
            // Default to all payment methods on error
            const defaultMethods = ["credit_card"];
            setAllowedPaymentMethods(defaultMethods);
            setExpandedAccordionItems(defaultMethods);
        }
    };    const getPaymentMethodData = async (method: string) => {

        let paymentMethodData = await supabase.functions.invoke(`stripe-payment-method?command=validate-payment-method&payment_method_id=${method}`,{
            method: "GET"
        });

        if (paymentMethodData.error) {
            console.error("Error fetching payment method data:", paymentMethodData.error);
            setHasAPaymentMethod(false);
            return;
        }

        setCardNumber("**** **** **** " + paymentMethodData.data.paymentMethod.card.last4 + " (" + paymentMethodData.data.paymentMethod.card.brand + ") " || "");

    };

    const fetchPaymentIntent = async () => {
        if (!currentOrganization?.id) return;

        setIsLoadingPaymentIntent(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-payment-method?command=get-payment-intent', {
                method: "GET"
            });

            if (error) {
                console.error("Error fetching payment intent:", error);
                toast({
                    title: "Error",
                    description: "Failed to initialize payment method setup",
                    variant: "destructive",
                });
                return;
            }

            if (data) {
                setPaymentIntentData({
                    client_secret: data.client_secret,
                    customer_session_client_secret: data.customer_session_client_secret
                });
                setIsSetupOfPaymentMethod(false); // Show the setup form
            }
        } catch (error: any) {
            console.error("Error fetching payment intent:", error);
            toast({
                title: "Error",
                description: "Failed to initialize payment method setup",
                variant: "destructive",
            });
        } finally {
            setIsLoadingPaymentIntent(false);
        }    };

    // New functions for plan management
    const fetchAvailablePlans = async () => {
        setIsLoadingPlans(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-subscription?command=get-products', {
                method: "GET"
            });

            if (error) {
                console.error("Error fetching plans:", error);
                toast({
                    title: "Error",
                    description: "Failed to load available plans",
                    variant: "destructive",
                });
                return;
            }

            if (data) {
                setAvailablePlans(data);
            }
        } catch (error: any) {
            console.error("Error fetching plans:", error);
            toast({
                title: "Error",
                description: "Failed to load available plans",
                variant: "destructive",
            });
        } finally {
            setIsLoadingPlans(false);
        }
    };

    const fetchCurrentSubscription = async () => {
        if (!currentOrganization?.id) return;

        setIsLoadingSubscription(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-subscription?command=get-subscription', {
                method: "GET"
            });

            if (error) {
                console.error("Error fetching subscription:", error);
                toast({
                    title: "Error",
                    description: "Failed to load subscription details",
                    variant: "destructive",
                });
                return;
            }

            if (data) {
                setCurrentSubscription(data);
            }
        } catch (error: any) {
            console.error("Error fetching subscription:", error);
            toast({
                title: "Error",
                description: "Failed to load subscription details",
                variant: "destructive",
            });
        } finally {
            setIsLoadingSubscription(false);
        }
    };    const fetchActiveLicenses = async () => {
        if (!currentOrganization?.id) return;

        try {
            const { data, error } = await supabase.functions.invoke('stripe-subscription?command=get-active-licenses', {
                method: "GET"
            });

            if (error) {
                console.error("Error fetching active licenses:", error);
                return;
            }

            if (data && typeof data.active_licenses === 'number') {
                setActiveLicenses(data.active_licenses);
            }
        } catch (error: any) {
            console.error("Error fetching active licenses:", error);
        }
    };

    const fetchInvoices = async () => {
        if (!currentOrganization?.id) return;

        setIsLoadingInvoices(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-subscription?command=get-invoices', {
                method: "GET"
            });

            if (error) {
                console.error("Error fetching invoices:", error);
                toast({
                    title: "Error",
                    description: "Failed to load billing history",
                    variant: "destructive",
                });
                return;
            }

            if (data && data.invoices) {
                // Filter out draft invoices and sort by creation date (newest first)
                const validInvoices = data.invoices
                    .filter((invoice: StripeInvoice) => invoice.status !== 'draft')
                    .sort((a: StripeInvoice, b: StripeInvoice) => b.created - a.created);
                setInvoices(validInvoices);
            }
        } catch (error: any) {
            console.error("Error fetching invoices:", error);
            toast({
                title: "Error",
                description: "Failed to load billing history",
                variant: "destructive",
            });
        } finally {
            setIsLoadingInvoices(false);
        }
    };

    const createSubscription = async () => {
        if (!currentOrganization?.id || !selectedPriceId) {
            toast({
                title: "Error",
                description: "Please select a plan first",
                variant: "destructive",
            });
            return;
        }

        setIsCreatingSubscription(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-subscription?command=create-subscription', {
                method: "POST",
                body: JSON.stringify({
                    organization: currentOrganization.id,
                    price_id: selectedPriceId,
                    discount_code: discountCode || undefined
                })
            });

            if (error) {
                console.error("Error creating subscription:", error);
                toast({
                    title: "Error",
                    description: "Failed to create subscription",
                    variant: "destructive",
                });
                return;
            }

            toast({
                title: "Success",
                description: "Subscription created successfully",
            });            // Refresh data
            await fetchSubscriptionData();
            await fetchCurrentSubscription();
            await fetchActiveLicenses();
            await fetchInvoices();
            
            // Reset form
            setSelectedPlan("");
            setSelectedPriceId("");
            setDiscountCode("");

        } catch (error: any) {
            console.error("Error creating subscription:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to create subscription",
                variant: "destructive",
            });
        } finally {
            setIsCreatingSubscription(false);
        }
    };

    const updateSubscription = async () => {
        if (!currentOrganization?.id || !selectedPriceId) {
            toast({
                title: "Error",
                description: "Please select a plan first",
                variant: "destructive",
            });
            return;
        }

        setIsUpdatingSubscription(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-subscription?command=update-subscription', {
                method: "POST",
                body: JSON.stringify({
                    organization: currentOrganization.id,
                    price_id: selectedPriceId,
                    discount_code: discountCode || undefined
                })
            });

            if (error) {
                console.error("Error updating subscription:", error);
                toast({
                    title: "Error",
                    description: "Failed to update subscription",
                    variant: "destructive",
                });
                return;
            }

            toast({
                title: "Success",
                description: "Subscription updated successfully",
            });            // Refresh data
            await fetchCurrentSubscription();
            await fetchActiveLicenses();
            await fetchInvoices();
            
            // Reset form
            setShowUpdatePlan(false);
            setSelectedPlan("");
            setSelectedPriceId("");
            setDiscountCode("");

        } catch (error: any) {
            console.error("Error updating subscription:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to update subscription",
                variant: "destructive",
            });
        } finally {
            setIsUpdatingSubscription(false);
        }
    };

    const cancelSubscription = async () => {
        setIsCancellingSubscription(true);
        try {
            const { error } = await supabase.functions.invoke('stripe-subscription?command=cancel-subscription', {
                method: "POST",
                body: JSON.stringify({})
            });

            if (error) {
                toast({
                    title: "Error",
                    description: "Failed to cancel subscription",
                    variant: "destructive",
                });
                return;
            }

            toast({
                title: "Subscription cancelled",
                description: "Your subscription has been cancelled successfully.",
            });

            await refreshUserData();
            await fetchSubscriptionData();
            await fetchCurrentSubscription();

        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to cancel subscription",
                variant: "destructive",
            });
        } finally {
            setIsCancellingSubscription(false);
        }
    };

    // Get the current plan details based on selectedPlan
    const currentPlan = availablePlans.find((plan) => plan.price_id === selectedPriceId);    const handlePlanChange = (planId: string, priceId: string) => {
        setSelectedPlan(planId);
        setSelectedPriceId(priceId);
    };// Group plans by product
    const groupedPlans = availablePlans.reduce((acc, plan) => {
        if (!acc[plan.product_id]) {
            acc[plan.product_id] = {
                product: plan.product_attrs,
                prices: []
            };
        }
        acc[plan.product_id].prices.push(plan);
        return acc;
    }, {} as Record<string, { product: any; prices: StripePlan[] }>);const options = {
        // passing the SetupIntent's client secret
        clientSecret: paymentIntentData?.client_secret || 'seti_1RXMCFP0M9NGrb7qd8hCAV6h_secret_SSGjeKZFRDzoKN5iSFt99qsPOoHjip0',
        customerSessionclientSecret: paymentIntentData?.customer_session_client_secret || 'cuss_secret_SSGjv86YIDLfmbwHd0euMOUJUKRqdG5dBz9vQCQxHfqGdAl',
        // Fully customizable with appearance API.
        appearance: {/*...*/},
    };    const exportAllInvoices = () => {
        if (invoices.length === 0) {
            toast({
                title: "No data to export",
                description: "There are no invoices to export",
                variant: "destructive",
            });
            return;
        }

        setIsExportingInvoices(true);
        try {
            // Create CSV content
            const headers = ['Invoice', 'Date', 'Amount', 'Currency', 'Status', 'Customer Name', 'Customer Email'];
            const csvContent = [
                headers.join(','),
                ...invoices.map(invoice => [
                    `"${invoice.number || invoice.id}"`,
                    `"${formatUnixTimestamp(invoice.created)}"`,
                    invoice.total / 100, // Convert from cents
                    invoice.currency.toUpperCase(),
                    `"${invoice.status}"`,
                    `"${invoice.customer_name || ''}"`,
                    `"${invoice.customer_email || ''}"`
                ].join(','))
            ].join('\n');

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `invoices-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast({
                title: "Export successful",
                description: "Invoices have been exported to CSV",
            });
        } catch (error) {
            console.error("Error exporting invoices:", error);
            toast({
                title: "Export failed",
                description: "Failed to export invoices",
                variant: "destructive",
            });
        } finally {
            setIsExportingInvoices(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Organization Information */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Building className="h-5 w-5" />
                        <CardTitle>{t("register.organization.title")}</CardTitle>
                    </div>
                    <CardDescription>
                        {t("register.organization.description")}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {isLoadingOrg ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="text-sm text-muted-foreground">Loading organization data...</div>
                        </div>
                    ) : (
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmitOrganization)} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("register.organization.name")} <span className="text-red-500">*</span></FormLabel>
                                                <FormControl>
                                                    <Input {...field} placeholder={t("register.organization.namePlaceholder")} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="invoice_email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("register.organization.invoiceEmail")} <span className="text-red-500">*</span></FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        type="email"
                                                        placeholder={t("register.organization.invoiceEmailPlaceholder")}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="vat_number"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("register.organization.vatNumber")} <span className="text-red-500">*</span></FormLabel>
                                                <FormControl>
                                                    <Input {...field} placeholder={t("register.organization.vatNumberPlaceholder")} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="country"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("register.organization.country")} <span className="text-red-500">*</span></FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder={t("register.organization.countryPlaceholder")} />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="Belgium">Belgium</SelectItem>
                                                        <SelectItem value="France">France</SelectItem>
                                                        <SelectItem value="Netherlands">Netherlands</SelectItem>
                                                        <SelectItem value="Germany">Germany</SelectItem>
                                                        <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                                                        <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                                                        <SelectItem value="Switzerland">Switzerland</SelectItem>
                                                        <SelectItem value="Other">Other</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="border-t pt-4 mt-6">
                                    <h3 className="text-md font-medium mb-4">{t("register.organization.addressTitle")}</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="attention"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("register.organization.attention")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} placeholder={t("register.organization.attentionPlaceholder")} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="street"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("register.organization.street")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} placeholder={t("register.organization.streetPlaceholder")} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="city"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("register.organization.city")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} placeholder={t("register.organization.cityPlaceholder")} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="zip"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("register.organization.zip")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} placeholder={t("register.organization.zipPlaceholder")} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button
                                        type="submit"
                                        disabled={isSubmittingOrg}
                                        className="bg-digger-blue hover:bg-digger-blue-light"
                                    >
                                        {isSubmittingOrg ? "Saving..." : t("common.save")}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    )}
                </CardContent>
            </Card>			

            {/* Payment Method */}

            {isAStripeCustomer && 

                <Card>
                    <CardHeader>
                        <CardTitle>{t("admin.billing.paymentMethod")}</CardTitle>
                        <CardDescription>
                            {t("admin.billing.paymentMethodDescription")}
                        </CardDescription>
                    </CardHeader>                    
                    <CardContent>                        
                        { hasAPaymentMethod && !paymentIntentData && 
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <CreditCard className="h-6 w-6 text-digger-blue" />
                                    <span className="text-lg font-semibold">Current Payment Method:</span>
                                    <span className="text-lg font-semibold">{cardNumber}</span>
                                </div>
                                <div className="flex justify-start">
                                    <Button 
                                        onClick={fetchPaymentIntent}
                                        disabled={isLoadingPaymentIntent}
                                        variant="outline"
                                        className="border-digger-blue text-digger-blue hover:bg-digger-blue hover:text-white"
                                    >
                                        {isLoadingPaymentIntent ? "Setting up..." : "Update Payment Method"}
                                    </Button>
                                </div>
                            </div>
                        }

                        { !hasAPaymentMethod && !isSetupOfPaymentMethod && !paymentIntentData && 
                            <div className="flex flex-col items-center gap-4 py-8">
                                <CreditCard className="h-12 w-12 text-muted-foreground" />
                                <div className="text-center">
                                    <p className="text-lg font-semibold mb-2">No Payment Method</p>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Add a payment method to manage your subscription and billing.
                                    </p>
                                    <Button 
                                        onClick={fetchPaymentIntent}
                                        disabled={isLoadingPaymentIntent}
                                        className="bg-digger-blue hover:bg-digger-blue-light"
                                    >
                                        {isLoadingPaymentIntent ? "Setting up..." : "Add Payment Method"}
                                    </Button>
                                </div>
                            </div>
                        }                        
                        
                        {!isSetupOfPaymentMethod && paymentIntentData && 
                            <div className="space-y-4">
                                <div className="text-center">
                                    <p className="text-lg font-semibold mb-2">
                                        {hasAPaymentMethod ? "Update Payment Method" : "Add Payment Method"}
                                    </p>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        {hasAPaymentMethod 
                                            ? "Enter your new payment method details below." 
                                            : "Enter your payment method details below."}
                                    </p>
                                </div>
                                <Elements stripe={stripePromise} options={options}>
                                    <SetupForm />
                                </Elements>
                                {hasAPaymentMethod && (
                                    <div className="flex justify-center pt-4">
                                        <Button 
                                            variant="ghost" 
                                            onClick={() => setPaymentIntentData(null)}
                                            className="text-muted-foreground hover:text-foreground"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                            </div>
                        }

                        { isSetupOfPaymentMethod &&
                            <Elements stripe={stripePromise} options={options}>
                                <PaymentStatus />
                            </Elements>
                        }

                    </CardContent>
                </Card>
            }            {/* Active Licenses - Always visible */}
            {isAStripeCustomer && (
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            <CardTitle>Active Licenses</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            <span className="text-3xl font-bold text-digger-blue mr-2">
                                {activeLicenses}
                            </span>
                            Currently active user licenses
                        </p>
                    </CardContent>
                </Card>
            )}
            
            {/* Plan Management Section */}
            {isAStripeCustomer && hasAPaymentMethod && (
                <>
                    {!hasAStripeSubscription && !showUpdatePlan && (
                        <PlanSelection
                            availablePlans={availablePlans}
                            isLoading={isLoadingPlans}
                            selectedPriceId={selectedPriceId}
                            discountCode={discountCode}
                            onPlanSelect={handlePlanChange}
                            onDiscountCodeChange={setDiscountCode}
                            onValidate={createSubscription}
                            isCreating={isCreatingSubscription}
                        />
                    )}                    {hasAStripeSubscription && !showUpdatePlan && currentSubscription && (
                        <CurrentSubscription
                            subscription={currentSubscription}
                            availablePlans={availablePlans}
                            isLoading={isLoadingSubscription}
                            onUpdateClick={() => setShowUpdatePlan(true)}
                            onCancelSubscription={cancelSubscription}
                            isCancelling={isCancellingSubscription}
                        />
                    )}

                    {showUpdatePlan && (
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Update Your Plan</CardTitle>
                                        <CardDescription>
                                            Choose a new plan for your organization
                                        </CardDescription>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        onClick={() => {
                                            setShowUpdatePlan(false);
                                            setSelectedPlan("");
                                            setSelectedPriceId("");
                                            setDiscountCode("");
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <PlanSelection
                                    availablePlans={availablePlans}
                                    isLoading={isLoadingPlans}
                                    selectedPriceId={selectedPriceId}
                                    discountCode={discountCode}
                                    onPlanSelect={handlePlanChange}
                                    onDiscountCodeChange={setDiscountCode}
                                    onValidate={updateSubscription}
                                    isCreating={isUpdatingSubscription}
                                />
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Billing History */}

            { isAStripeCustomer && hasAStripeSubscription && hasAPaymentMethod &&
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>{t("admin.billing.billingHistory")}</CardTitle>
                            <CardDescription>
                                {t("admin.billing.billingHistoryDescription")}
                            </CardDescription>
                        </div>                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={exportAllInvoices}
                                disabled={isLoadingInvoices || invoices.length === 0 || isExportingInvoices}
                            >
                                <Download className="h-4 w-4 mr-2" />
                                {isExportingInvoices ? "Exporting..." : t("admin.billing.exportAll")}
                            </Button>
                        </div>
                    </CardHeader>                    <CardContent>
                        {isLoadingInvoices ? (
                            <div className="flex h-24 items-center justify-center text-muted-foreground">
                                {t("admin.billing.loadingInvoices") || "Loading invoices..."}
                            </div>
                        ) : invoices.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t("admin.billing.invoice")}</TableHead>
                                        <TableHead>{t("admin.billing.date")}</TableHead>
                                        <TableHead>{t("admin.billing.amount")}</TableHead>
                                        <TableHead>{t("admin.billing.status")}</TableHead>
                                        <TableHead className="text-right">{t("admin.billing.actions")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {invoices.map((invoice) => (
                                        <TableRow key={invoice.id}>
                                            <TableCell className="font-medium">
                                                {invoice.number || invoice.id}
                                            </TableCell>
                                            <TableCell>{formatUnixTimestamp(invoice.created)}</TableCell>
                                            <TableCell>
                                                {formatPrice(invoice.total, invoice.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={invoice.status === "paid" ? "default" : "secondary"}
                                                    className={
                                                        invoice.status === "paid"
                                                            ? "bg-green-100 text-green-800 hover:bg-green-100"
                                                            : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                                                    }
                                                >
                                                    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                                                    disabled={!invoice.hosted_invoice_url}
                                                >
                                                    <Download className="h-4 w-4" />
                                                    <span className="sr-only">{t("admin.billing.downloadAction")}</span>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="flex h-24 items-center justify-center text-muted-foreground">
                                {t("admin.billing.noInvoices")}
                            </div>
                        )}
                    </CardContent>
                </Card>
            }
            
        </div>
    );
};